/**
 * Copyright 2025 Lumavec Group LLC
 *
 * TemplateSelector Component
 * 
 * Displays a grid of available templates for selection.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { TemplateCard } from './TemplateCard';
import { listTemplates, type TemplateMetadata } from '../../lib/api';

interface TemplateSelectorProps {
  selectedTemplate: string | null;
  onSelectTemplate: (template: TemplateMetadata) => void;
}

export function TemplateSelector({ selectedTemplate, onSelectTemplate }: TemplateSelectorProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const result = await listTemplates();
      if (!result.success || !result.templates) {
        throw new Error(result.error || 'Failed to load templates');
      }
      return result.templates;
    },
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
        <span className="ml-3 text-muted-foreground">Loading templates...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-2">Failed to load templates</p>
        <p className="text-sm text-muted-foreground">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }
  
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No templates available</p>
      </div>
    );
  }
  
  // Group templates by category
  const templatesByCategory = data.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, TemplateMetadata[]>);
  
  const categoryOrder = ['web', 'desktop', 'platform', 'mobile', 'api'];
  const sortedCategories = Object.keys(templatesByCategory).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );
  
  return (
    <div className="space-y-8">
      {sortedCategories.map((category) => (
        <div key={category}>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
            {getCategoryLabel(category)}
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {templatesByCategory[category].map((template) => (
              <TemplateCard
                key={template.name}
                template={template}
                isSelected={selectedTemplate === template.name}
                onSelect={() => onSelectTemplate(template)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function getCategoryLabel(category: string): string {
  switch (category) {
    case 'web': return 'Web Applications';
    case 'desktop': return 'Desktop Applications';
    case 'platform': return 'Platform Applications';
    case 'mobile': return 'Mobile Applications';
    case 'api': return 'API Services';
    default: return category;
  }
}

export default TemplateSelector;

